// src/components/OrderCommentsSection.js
import React, { useState, useEffect } from "react";
import "./OrderCommentsSection.css";
import CommentHistoryModal from "./CommentHistoryModal";
import { deleteComment } from "../assets";   // ← nasza nowa ikonka
import { API_URL } from "../config";

/**
 * Komponent sekcji komentarzy dla zlecenia.
 * @param {number} orderId
 * @param {Object} currentUser  // musi zawierać .id i .role
 */
function OrderCommentsSection({ orderId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");
  const [historyModalCommentId, setHistoryModalCommentId] = useState(null);

  // fetch komentarzy
  useEffect(() => {
    setLoading(true);
    fetch(
      `${API_URL}/orders/${orderId}/comments`,
      { credentials: "include" }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Błąd pobierania komentarzy.");
        return res.json();
      })
      .then(setComments)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [orderId]);

  // dodawanie komentarza
  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return setSubmitError("Komentarz nie może być pusty.");

    try {
      const res = await fetch(
        `${API_URL}/orders/${orderId}/comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ comment_text: newComment.trim() }),
        }
      );
      if (!res.ok) throw new Error("Błąd podczas dodawania komentarza.");
      const added = await res.json();
      setComments((prev) => [...prev, added]);
      setNewComment("");
      setSubmitError("");
    } catch (err) {
      setSubmitError(err.message);
    }
  };

  // zapisz edycję
  const handleSaveComment = async (id) => {
    if (!editedCommentText.trim()) return setSubmitError("Komentarz nie może być pusty.");
    try {
      const res = await fetch(
        `${API_URL}/orders/${orderId}/comments/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ comment_text: editedCommentText.trim() }),
        }
      );
      if (!res.ok) throw new Error("Błąd podczas zapisywania komentarza.");
      // odśwież listę
      const updated = await fetch(
        `${API_URL}/orders/${orderId}/comments`,
        { credentials: "include" }
      ).then((r) => r.json());
      setComments(updated);
      setEditingCommentId(null);
      setEditedCommentText("");
      setSubmitError("");
    } catch (err) {
      setSubmitError(err.message);
    }
  };

  // usuwanie komentarza (tylko Admin)
  const handleDeleteComment = async (id) => {
    if (!window.confirm("Na pewno chcesz usunąć ten komentarz?")) return;
    try {
      const res = await fetch(
        `${API_URL}/orders/${orderId}/comments/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!res.ok) throw new Error("Błąd podczas usuwania komentarza.");
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="comments-section">
      <h3>Komentarze</h3>

      <div className="comments-list">
        {loading ? (
          <p>Ładowanie...</p>
        ) : error ? (
          <p style={{ color: "red" }}>{error}</p>
        ) : comments.length === 0 ? (
          <p>Brak komentarzy pod tym zleceniem.</p>
        ) : (
          comments.map((comment) => {
            const isOwn = comment.user_id === currentUser.id;
            const isEditing = editingCommentId === comment.id;
            return (
              <div
                key={comment.id}
                className={[
                  "comment",
                  isOwn ? "comment-own" : "comment-other",
                  isEditing ? "editing" : "",
                ].join(" ")}
              >
                <div className="comment-header">
                  <span className="comment-author">
                    {comment.user_first_name} {comment.user_last_name }
                  </span>

                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleString("pl-PL")}
                  </span>

                  {/* delete tylko dla Admina */}
                  {currentUser.role === "Admin" && (
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <img src={deleteComment} alt="Usuń komentarz" />
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="comment-edit">
                    <textarea
                      className="comment-textarea"
                      rows="4"
                      value={editedCommentText}
                      onChange={(e) => setEditedCommentText(e.target.value)}
                    />
                    <div className="comment-actions">
                      <button
                        onClick={() => handleSaveComment(comment.id)}
                        className="save-button"
                      >
                        Zapisz
                      </button>
                      <button
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditedCommentText("");
                          setSubmitError("");
                        }}
                        className="cancel-button"
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="comment-text">{comment.comment_text}</p>
                )}

                <div className="comment-actions">
                  {!isEditing && comment.user_id === currentUser.id && (
                    <button
                      onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditedCommentText(comment.comment_text);
                      }}
                      className="edit-button"
                    >
                      Edytuj
                    </button>
                  )}
                  {!isEditing && comment.has_edits && (
                    <button
                      onClick={() => setHistoryModalCommentId(comment.id)}
                      className="history-button"
                    >
                      Historia
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <form className="comment-form" onSubmit={handleAddComment}>
        <textarea
          className="comment-textarea"
          rows="4"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Dodaj komentarz..."
        />
        {submitError && <p style={{ color: "red" }}>{submitError}</p>}
        <button type="submit" className="submit-button">
          Dodaj komentarz
        </button>
      </form>

      {historyModalCommentId && (
        <CommentHistoryModal
          orderId={orderId}
          commentId={historyModalCommentId}
          isOpen
          onClose={() => setHistoryModalCommentId(null)}
        />
      )}
    </div>
  );
}

export default OrderCommentsSection;
